import axios from 'axios'
import type {
  AvailableAddons,
  Discount,
  InsurerProductPartAvailable,
  Offer,
  OfferDiscounts,
  OfferPackage,
  OfferProperty,
  OfferTravel,
  OfferVehicle,
  PackageItemModel,
  PhotoDocumentation,
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { longStaleTime } from 'BrokerTsLib/src/hooks'
import { useMutation, useQuery } from 'react-query'

import { baseUrl } from '.'
import { useApp } from './useApp'

const offerApiUrl = import.meta.env.VITE_OFFER_API
const offerTravelApiUrl = import.meta.env.VITE_OFFER_TRAVEL_API
const offerPropertyApiUrl = import.meta.env.VITE_OFFER_PROPERTY_API
const offerLiabilityApiUrl = import.meta.env.VITE_OFFER_LIABILITY_API

export type CalculateOffersRequest = {
  hubId: string
  offerIds?: number[]
  forceCalculation?: boolean
  paramsId?: string
}

export const useUrl = () => {
  const { product } = useApp()

  let url: string | undefined = ''

  switch (product) {
    case Product.TravelInsurance:
      url = offerTravelApiUrl
      break
    case Product.PropertyInsurance:
      url = offerPropertyApiUrl
      break
    case Product.LiabilityInsurance:
      url = offerLiabilityApiUrl
      break
    default:
      url = offerApiUrl
      break
  }

  return `${baseUrl}/${url}`
}

export type OffersType = Offer | OfferVehicle | OfferPackage | OfferProperty | OfferTravel

export const useGetOffers = (paramsId: string, options?: GenericQueryOptions<OffersType[]>) => {
  const url = useUrl()
  return useQuery(
    ['getOffers', paramsId],
    () => axios.get<OffersType[]>(`${url}/GetOffers?paramsId=${paramsId}`),
    options,
  )
}

export const useGetOffer = (offerId: number, options?: GenericQueryOptions<OffersType>) => {
  const url = useUrl()
  return useQuery(
    ['getCalculation', offerId],
    () => axios.get<OffersType>(`${url}/GetOffer?offerId=${offerId}`),
    options,
  )
}

export const useGetOfferValid = (offerId: number, options?: GenericQueryOptions<boolean>) => {
  const url = useUrl()
  return useQuery({
    queryKey: ['useGetOfferValid', offerId],
    queryFn: async () => {
      const resp = await axios.get<boolean>(`${url}/GetInsurerOfferValid?insurerOfferId=${offerId}`)
      return resp.data
    },
    ...options
  })
}

export const useCalculateOffers = () => {
  const url = useUrl()
  return useMutation((data: CalculateOffersRequest) =>
    axios.post<OffersType[]>(`${url}/CalculateOffers`, data),
  )
}

export const useSelectCalculation = () => {
  const url = useUrl()
  return useMutation((data: { paramsId: string; offerId: number }) =>
    axios.post(`${url}/SelectOffer`, data),
  )
}

export const useGetOfferAvailableDiscounts = (
  offerId: number,
  options?: GenericQueryOptions<Discount[]>,
) => {
  const url = useUrl()

  return useQuery(
    ['GetOfferAvailableDiscounts', offerId],
    () => axios.get<Discount[]>(`${url}/GetOfferAvailableDiscounts?offerId=${offerId}`),
    { staleTime: longStaleTime, ...options },
  )
}

export const useSaveOfferDiscounts = (offerId: number) => {
  const url = useUrl()
  return useMutation((data: OfferDiscounts) =>
    axios.post(`${url}/SaveOfferDiscounts?offerId=${offerId}`, data),
  )
}

export const useGetOfferAvailableAddons = (
  offerId: number,
  options?: GenericQueryOptions<AvailableAddons[] | PackageItemModel[]>,
) => {
  const url = useUrl()
  return useQuery(
    ['GetOfferAvailableAddons', offerId],
    () =>
      axios.get<AvailableAddons[] | PackageItemModel[]>(
        `${url}/GetOfferAvailableAddons?offerId=${offerId}`,
      ),
    { staleTime: longStaleTime, ...options },
  )
}

export const useSaveOfferAddons = (offerId: number) => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: InsurerProductPartAvailable[]) =>
      axios.post(`${url}/SaveOfferAddons?offerId=${offerId}`, data),
  })
}

export const useSetOfferIncluded = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: { offerId: number; included: boolean }) =>
      axios.post(`${url}/SetOfferIncluded`, data),
  })
}

export const useSetOfferPaymentFrequency = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: { offerId: number; paymentFrequency: number }) =>
      axios.post(`${url}/SetOfferPaymentFrequency`, data),
  })
}

export const useGetOfferCkp = (offerId: number) => {
  const url = useUrl()
  return useMutation({
    mutationFn: () =>
      axios.get<{
        ckpMonths?: number
        ckpClaims?: number
        message?: string
      }>(`${url}/GetOfferCkp?offerId=${offerId}`),
  })
}

export const useClone = (offerId: number) => {
  const url = useUrl()
  return useMutation({
    mutationFn: () => axios.post<OffersType>(`${url}/Clone?offerId=${offerId}`),
  })
}

export const useGetVehicleInspection = (
  offerId: number,
  options?: GenericQueryOptions<PhotoDocumentation[]>,
) => {
  const url = useUrl()
  return useQuery(
    ['useGetVehicleInspection', offerId],
    () => axios.get<PhotoDocumentation[]>(`${url}/GetVehicleInspection?offerId=${offerId}`),
    { staleTime: longStaleTime, ...options },
  )
}

export const useSetOfferYearKm = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: { offerId: number; yearKm: string }) =>
      axios.post(`${url}/SetOfferYearKm`, data),
  })
}

// TODO: nahradit za useResolveChangedParts
export const useResolveChangedPart = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: { offer: OfferPackage; packageItemId: number }) =>
      axios.post<OfferPackage>(`${url}/ResolveChangedPart`, data),
  })
}

export const useResolveChangedParts = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: { offer: OfferPackage; packageItemsIds: number[] }) =>
      axios.post<OfferPackage>(`${url}/ResolveChangedParts`, data),
  })
}
